import Title_ru_RU         from '@/core/misc/decorators/Title_ru_RU';
import Title_ru_RU_Class   from '@/core/misc/decorators/Title_ru_RU_Class';
import SerializeSimplified from '@/core/misc/decorators/SerializeSimplified';

import TYPES from '@/core/services/Changelogs/types';

import Core       from '@/core/Core';
import BaseEntity from '@/core/misc/BaseEntity';
import DateSimple from '@/core/types/DateSimple';
import Qty        from '@/core/types/Qty';

import Partner         from '@/domains/Partners/Partner';
import Company         from '@/domains/Companies/Company';
import Product         from '@/domains/Products/Product';
import Manufacturer    from '@/domains/Manufacturers/Manufacturer';
import TradeMark       from '@/domains/TradeMarks/TradeMark';
import Unit            from '@/domains/Units/Unit';
import Person          from '@/domains/Persons/Person';

import SupplyOfferTariff   from '@/domains/SupplyOffers/SupplyOfferTariff';
import SupplyOffersService from '@/domains/SupplyOffers/SupplyOffersService';
import SupplyOfferStatus from '@/domains/SupplyOffers/SupplyOfferStatus';

/**
 * Разрешение какому-то поставщику на то чтобы возить какой-то товар,
 * на каких условиях и по каким ценам.
 * Объединяет прежние PriceItem (позиция прайса) и SupplyPermission (разрешение на поставку).
 */
@Title_ru_RU_Class('Разрешение на поставку')
class SupplyOffer extends BaseEntity {

  static Events = {
    Created: 'supplyOffer:created',
    Updated: 'supplyOffer:updated',
    Deleted: 'supplyOffer:deleted',
  } as const;

  /** Статус одобренного разрешения */
  static readonly STATUS_APPROVED = '02_approved';

  /** поставщик */
  @Title_ru_RU('поставщик', TYPES.OBJECT)
  @SerializeSimplified
  public seller: Partner | null;

  /** юр лицо поставщика */
  @Title_ru_RU('юр лицо поставщика', TYPES.OBJECT)
  @SerializeSimplified
  public sellerCompany: Company | null;

  /** продукт, который разрешается поставлять */
  @Title_ru_RU('товар', TYPES.OBJECT)
  @SerializeSimplified
  public product: Product | null;

  /** Наименование товара в ИС поставщика (для товаров без каталога) */
  @Title_ru_RU('наименование товара в ИС поставщика', TYPES.STRING)
  public name: string;

  /** Единица измерения */
  @Title_ru_RU('единица измерения', TYPES.OBJECT)
  @SerializeSimplified
  public unit: Unit;

  /** производитель */
  @Title_ru_RU('производитель', TYPES.OBJECT)
  @SerializeSimplified
  public manufacturer: Manufacturer | null;

  /** торговая марка */
  @Title_ru_RU('торговая марка', TYPES.OBJECT)
  @SerializeSimplified
  public tradeMark: TradeMark | null;

  /** статусы, разрешение может быть отклонено / в процессе получения / одобрено */
  @Title_ru_RU('статус', TYPES.OBJECT)
  public status: SupplyOfferStatus | null;

  /** статус доступности */
  @Title_ru_RU('статус доступности', TYPES.STRING)
  public statusAvailability: any | null;

  /** Основной или альтернативный */
  @Title_ru_RU('основной или альтернативный', TYPES.BOOLEAN)
  public isMain: boolean = true;

  /** Поставщик не допущен к тендерам по конкретному продукту */
  @Title_ru_RU('не допущен к тендерам', TYPES.BOOLEAN_MARK)
  public isExcludedFromTenders: boolean = false;

  /** Ссылка на задачу в жире по тестированию сырья, ну или на что еще в голову взбредет */
  @Title_ru_RU('ссылка', TYPES.STRING)
  public relatedUrl: string | null;

  /** Дней поставки до склада поставщика */
  @Title_ru_RU('дни поставки', TYPES.NUMBER)
  public deliveryDaysToStore: number;

  /** Минимальный объем заказа */
  @Title_ru_RU('минимальный объем заказа', TYPES.OBJECT)
  public minOrderVolume: Qty|null = null;

  /** Фасовка производителя — в чём поставляет производитель. Требование производства — в номенклатуре */
  @Title_ru_RU('фасовка производителя', TYPES.OBJECT)
  public packageVolume: Qty|null = null;

  /** Ответственный менеджер */
  // upd я не знаю зачем он нужен, зачем он нужен?
  @SerializeSimplified
  public responsible: Person;

  /** Тарифы */
  @Title_ru_RU('тарифы', TYPES.TARIFFS)
  public tariffs: SupplyOfferTariff[] = [];

  /**
   * Создать экземпляр из данных сервера
   */
  static fromServer(data: any, offer: SupplyOffer = new SupplyOffer()): SupplyOffer {
    super.fromServer(data, offer);

    offer.seller = Core.utils.hydrate(data.seller, Partner.fromServer);

    if (data.status) {
      offer.status = SupplyOffersService.statuses.get(data.status.id) ?? null;
    }

    if (data.statusAvailability) {
      offer.statusAvailability = SupplyOffersService.statusesAvailability.get(data.statusAvailability.id);
    }

    offer.sellerCompany   = Core.utils.hydrate(data.sellerCompany,   Company.fromServer);
    offer.product         = Core.utils.hydrate(data.product,         Product.fromServer);
    offer.unit            = Core.utils.hydrate(data.unit,            Unit.fromServer)!;
    offer.manufacturer    = Core.utils.hydrate(data.manufacturer,    Manufacturer.fromServer);
    offer.tradeMark       = Core.utils.hydrate(data.tradeMark,       TradeMark.fromServer);
    offer.status          = Core.utils.hydrate(data.status, SupplyOfferStatus.fromServer);
    offer.responsible     = Core.utils.hydrate(data.responsible,     Person.fromServer)!;
    offer.packageVolume   = Core.utils.hydrate(data.packageVolume,   Qty.fromServer)!;
    offer.minOrderVolume  = Core.utils.hydrate(data.minOrderVolume,  Qty.fromServer);

    offer.tariffs         = Core.utils.hydrateArray(data.tariffs, SupplyOfferTariff.fromServer);

    return offer;
  }

  /**
   * Создать новое разрешение для контекста, откуда открыли создание:
   * из карточки товара известен товар, из карточки поставщика — поставщик.
   */
  static fromSellerProduct(seller?: Partner | null, product?: Product | null): SupplyOffer {
    const result = new this();

    if (product) {
      result.product = product;
      result.unit = product.unit;
    }

    if (seller) {
      result.seller = seller;
    }


    result.responsible = Core.currentUser;

    result.status             = SupplyOffersService.getDefaultStatus();
    result.statusAvailability = SupplyOffersService.getDefaultStatusAvailability();

    return result;
  }

  /** ================ Связка ================ */

  /** Разрешение одобрено. */
  get isApproved(): boolean {
    return this.status?.id === SupplyOffer.STATUS_APPROVED;
  }

  /** Ключ связки: поставщик + юр. лицо + производитель. */
  get bundleKey(): string {
    return SupplyOffer.getBundleKey(this.seller!, this.sellerCompany!, this.manufacturer!);
  }

  /**
   * Ключ связки по отдельным полям — чтобы сравнить с чужой тройкой.
   * Поставщик, юр. лицо и производитель неразрывны: взять производителя
   * из одного разрешения, а юр. лицо из другого нельзя.
   */
  static getBundleKey(seller: Partner, sellerCompany: Company, manufacturer: Manufacturer): string {
    return [seller.id, sellerCompany.id, manufacturer.id].join(':');
  }

  /** Связка строкой: поставщик — юр. лицо — производитель. */
  toBundleString(): string {
    return [
      this.seller!.name,
      this.sellerCompany!.name,
      this.manufacturer!.name,
    ].join(' — ');
  }

  static getIsMainText(isMain: boolean): string {
    if (isMain) {
      return 'Основной';
    }
    else {
      return 'Альтернативный';
    }
  }

  get isExpired() {
    return Core.utils.date.dateDifference(this.updatedAt, DateSimple.today()) > SupplyOffersService.PRICE_EXPIRATION_DAYS;
  }

  /** Тарифы, сгруппированные по валюте и отсортированные по qty.start по возрастанию */
  get sortedTariffs(): SupplyOfferTariff[] {
    return [...this.tariffs].sort((a, b) => {
      const currencyOrder = a.currencyCode.localeCompare(b.currencyCode);

      if (currencyOrder !== 0) {
        return currencyOrder;
      }
      else {
        return (a.qty?.start ?? 0) - (b.qty?.start ?? 0);
      }
    });
  }

  get formattedNumber(): string {
    if (this.id) {
      return `№${Core.utils.string.formatWithDelimiter(this.id)}`;
    }
    else {
      return '';
    }
  }

  toString(): string {
    let productName;

    if (this.product) {
      productName = this.product.name;
    }
    else {
      productName = '';
    }

    return `${this.entityTypeName} по ${productName}`;
  }

  /** Добавляет новый тариф в список */
  addTariff(tariff: SupplyOfferTariff): void {
    this.tariffs.push(tariff);
  }

  /**
   * Кладёт тариф из карточки: отредактированный заменяет свой оригинал, новый (original === null) добавляется
   */
  putTariff(tariff: SupplyOfferTariff, original: SupplyOfferTariff | null): void {
    let index: number;

    if (original === null) {
      index = -1;
    }
    else {
      index = this.tariffs.indexOf(original);
    }

    if (index === -1) {
      this.addTariff(tariff);
    }
    else {
      this.tariffs.splice(index, 1, tariff);
    }
  }
}

export default SupplyOffer;
